import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  ExternalLink,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Badge } from "@v2/components/ui/badge";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Textarea } from "@v2/components/ui/textarea";
import { AttendanceBar, CountMark, SessionDate, StatTileLabel, TimeSections } from "./parts";
import { ShareTextDialog } from "@v2/components/ShareTextDialog";
import { currentIntlLocale } from "../../lib/i18n";
import { formatDateTime } from "../../lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { track } from "../../lib/analytics";
import { statusLabel } from "../../lib/labels";
import { CallupOverview, CallupTiles } from "./CallupOverview";
import { isEvaluatingGuest, isSquadPlayer } from "../../lib/roster";
import { useUsersDisplay, userShortName } from "../../lib/users-display";
import { useWorkspaceData } from "../../lib/workspace-data";
import { seasonErrorMessage } from "../../lib/seasons";
import {
  shiftIsoDays,
  useCallupPresentMutation,
  useMagicLinkMutation,
  useNotifyCallupsMutation,
  useSessionCallupUpsert,
  useSessionCallups,
  useSessionMessageMutation,
  useCallupListMessageMutation,
  useSessionUpdateMutation,
} from "../../lib/sessions";
import { EmptyState, SectionLoading } from "../../components/state";
import type {
  Player,
  ResponseStatus,
  Session,
  SessionCallup,
  SessionResponse,
} from "../../types";

export function EventsView({ teamId }: { teamId: string }) {
  const { t } = useTranslation("sessions");
  const queryClient = useQueryClient();
  const { players, events, responses, attendance, isLoading } = useWorkspaceData(teamId);
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedMessageEventId, setCopiedMessageEventId] = useState<string | null>(null);
  const [shareDialog, setShareDialog] = useState<{ open: boolean; title: string; text: string }>({
    open: false,
    title: "",
    text: "",
  });
  const [pastPage, setPastPage] = useState(1);
  const PAST_PAGE_SIZE = 10;
  const trainingEvents = events
    .filter((event) => !event.has_matches)
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  const callupsQuery = useSessionCallups(teamId);

  const duplicateEvent = useMutation({
    mutationFn: async (event: Session) => {
      const { data: authData } = await supabaseV2.auth.getUser();
      const { error } = await supabaseV2.from("sessions").insert({
        team_id: teamId,
        title: `${event.title} copia`,
        starts_at: shiftIsoDays(event.starts_at, 7),
        ends_at: shiftIsoDays(event.ends_at, 7),
        location: event.location,
        communication_details: event.communication_details,
        notes: event.notes,
        response_deadline_at: shiftIsoDays(event.response_deadline_at || event.starts_at, 7),
        status: "scheduled",
        created_by: authData.user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "all"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "training"] });
    },
    onError: (e: any) => window.alert(seasonErrorMessage(e) || e?.message || String(e)),
  });
  const deleteEvent = useMutation({
    mutationFn: async (eventId: string) => {
      const { error } = await supabaseV2.from("sessions").delete().eq("id", eventId).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: () => {
      setOpenEventId(null);
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "all"] });
      queryClient.invalidateQueries({ queryKey: ["events", teamId, "training"] });
      queryClient.invalidateQueries({ queryKey: ["responses", teamId] });
    },
  });
  const updateEvent = useSessionUpdateMutation(teamId);
  const updateResponse = useMutation({
    mutationFn: async ({ eventId, playerId, status }: { eventId: string; playerId: string; status: ResponseStatus }) => {
      const { error } = await supabaseV2.from("session_responses").upsert(
        {
          session_id: eventId,
          player_id: playerId,
          status,
          source: "staff_override",
          responded_at: status === "no_response" ? null : new Date().toISOString(),
        },
        { onConflict: "session_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["responses", teamId] });
    },
  });
  const createMagicLink = useMagicLinkMutation(teamId);
  const copySessionMessage = useSessionMessageMutation();
  const updateCallup = useSessionCallupUpsert(teamId);
  const callupPresent = useCallupPresentMutation(teamId);
  const copyCallupList = useCallupListMessageMutation();
  const notifyCallups = useNotifyCallupsMutation(teamId);
  const [copiedCallupsEventId, setCopiedCallupsEventId] = useState<string | null>(null);

  const onNotifyCallupsForEvent = (sessionId: string, pendingCount: number) => {
    if (pendingCount === 0) return;
    if (!window.confirm(t("board.notifyCallupsConfirm", { count: pendingCount }))) return;
    notifyCallups.mutate(sessionId, {
      onSuccess: ({ sent, already }) => {
        track.callupSent({ team_id: teamId, session_id: sessionId, sent, already });
        const msg =
          already > 0
            ? t("board.notifyCallupsResultOkWithAlready", { sent, already })
            : t("board.notifyCallupsResultOk", { sent });
        window.alert(msg);
      },
      onError: (e: any) => {
        window.alert(`${t("board.notifyCallupsError")}: ${e?.message || e}`);
      },
    });
  };

  const rosterPlayers = players.filter(isSquadPlayer);
  const guestPlayers = players.filter(isEvaluatingGuest);
  const trainingIds = new Set(trainingEvents.map((e) => e.id));
  const trainingResponses = responses.filter((r) => trainingIds.has(r.session_id));
  const nowMs = Date.now();
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const pastTraining = trainingEvents.filter((e) => new Date(e.starts_at).getTime() < nowMs);
  const upcomingTraining = trainingEvents
    .filter((e) => new Date(e.starts_at).getTime() >= nowMs)
    .slice()
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  type PlayerStat = { presentLate: number; late: number; absent: number; noResponse: number };
  const playerStats = new Map<string, PlayerStat>();
  for (const p of rosterPlayers) {
    playerStats.set(p.id, { presentLate: 0, late: 0, absent: 0, noResponse: 0 });
  }
  let totalPresentLate = 0;
  let totalExpectedSlots = 0;
  for (const event of pastTraining) {
    const respByPlayer = new Map(
      trainingResponses.filter((r) => r.session_id === event.id).map((r) => [r.player_id, r] as const),
    );
    // Base della percentuale di presenza: chi ha un callup 'confirmed'
    // oppure chi ha già una risposta (presence/absent/late).
    const expectedIds = new Set<string>();
    for (const c of callupsQuery.data || []) {
      if (c.session_id === event.id && c.status === "confirmed") expectedIds.add(c.player_id);
    }
    for (const r of respByPlayer.values()) {
      if (r.status !== "no_response") expectedIds.add(r.player_id);
    }
    for (const playerId of expectedIds) {
      const ps = playerStats.get(playerId);
      if (!ps) continue;
      const r = respByPlayer.get(playerId);
      totalExpectedSlots += 1;
      if (!r || r.status === "no_response") continue;
      if (r.status === "absent") ps.absent += 1;
      else if (r.status === "late") {
        ps.late += 1;
        ps.presentLate += 1;
        totalPresentLate += 1;
      } else if (r.status === "present") {
        ps.presentLate += 1;
        totalPresentLate += 1;
      }
    }
  }
  // Non risposte: la stessa base di Rosa e Statistiche. Le convocazioni si fanno
  // dopo le risposte, quindi non dicono chi era atteso.
  for (const r of attendance) {
    if (r.status === "no_response" && trainingIds.has(r.session_id)) {
      const ps = playerStats.get(r.player_id);
      if (ps) ps.noResponse += 1;
    }
  }
  const trainingsThisMonth = pastTraining.filter((e) => new Date(e.starts_at).getTime() >= startOfMonth.getTime()).length;
  const attendancePct = totalExpectedSlots > 0 ? Math.round((totalPresentLate / totalExpectedSlots) * 100) : 0;
  // Classifica per i riquadri espandibili: primi 5 con valore > 0, a pari
  // valore in ordine alfabetico.
  const ranking = (key: keyof PlayerStat) =>
    rosterPlayers
      .map((p) => ({ name: p.display_name, value: playerStats.get(p.id)?.[key] || 0 }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
      .slice(0, 5);
  const presentRanking = ranking("presentLate");
  const absentRanking = ranking("absent");
  const lateRanking = ranking("late");
  const noResponseRanking = ranking("noResponse");
  const mostPresent = presentRanking[0];
  const mostAbsent = absentRanking[0];
  const mostLate = lateRanking[0];
  const mostNoResponse = noResponseRanking[0];
  const totalCallupsConfirmed = (callupsQuery.data || []).filter(
    (c) => c.status === "confirmed" && trainingIds.has(c.session_id),
  ).length;
  const avgCallups = pastTraining.length > 0 ? Math.round(totalCallupsConfirmed / pastTraining.length) : 0;

  const totalPastPages = Math.max(1, Math.ceil(pastTraining.length / PAST_PAGE_SIZE));
  const safePage = Math.min(pastPage, totalPastPages);
  const pastSlice = pastTraining.slice((safePage - 1) * PAST_PAGE_SIZE, safePage * PAST_PAGE_SIZE);
  const visibleEvents = safePage === 1 ? [...upcomingTraining, ...pastSlice] : pastSlice;
  const creatorsMap = useUsersDisplay(visibleEvents.map((e) => e.created_by));
  const intlLocale = currentIntlLocale();
  const todayKey = new Date().toDateString();

  return (
    <div className="space-y-3 animate-fade-in">
      <section className="flex flex-wrap gap-2 items-center">
        <p className="text-[13px] text-[hsl(var(--faint))]">
          {t("events.summary", { total: trainingEvents.length, done: pastTraining.length })}
          {upcomingTraining.length > 0 ? t("events.summarySuffix", { count: upcomingTraining.length }) : ""}
        </p>
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("events.searchPlaceholder")}
              className="pl-9"
            />
          </div>
        </div>
      </section>

      {isLoading ? (
        <SectionLoading />
      ) : trainingEvents.length === 0 ? (
        <EmptyState text={t("events.empty")} />
      ) : (
        <div className="flex flex-col gap-2.5">
          <TimeSections items={visibleEvents} render={(event) => {
            const eventResponses = responses.filter((r) => r.session_id === event.id);
            const counts = getResponseCounts(eventResponses, rosterPlayers.length);
            const isOpen = openEventId === event.id;
            const eventStart = new Date(event.starts_at);
            const eventStartMs = eventStart.getTime();
            const isPast = eventStartMs < nowMs;
            const isToday = !isPast && eventStart.toDateString() === todayKey;
            const eventCallups = (callupsQuery.data || []).filter(
              (c) => c.session_id === event.id && c.status === "confirmed",
            );
            const callupCount = eventCallups.length;
            const pendingNotifyCount = eventCallups.filter((c) => !c.notified_at).length;
            const deadlineDate = event.response_deadline_at ? new Date(event.response_deadline_at) : null;
            const deadlinePassed = !!deadlineDate && deadlineDate.getTime() < Date.now();
            const totalSlots = Math.max(rosterPlayers.length, 1);
            const presentPct = Math.min(100, (counts.present / totalSlots) * 100);
            const latePct = Math.min(100 - presentPct, (counts.late / totalSlots) * 100);
            const absentPct = Math.min(
              100 - presentPct - latePct,
              (counts.absent / totalSlots) * 100,
            );
            const creator = event.created_by ? creatorsMap.get(event.created_by) : null;
            const onCopyMessageClick = () =>
              copySessionMessage.mutate(event, {
                onSuccess: ({ text, title }) => {
                  setCopiedMessageEventId(event.id);
                  setShareDialog({ open: true, title, text });
                },
              });
            return (
              <Card key={event.id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenEventId(isOpen ? null : event.id)}
                  className="w-full text-left hover:bg-raised/60 transition-colors"
                >
                  <div className="flex items-center gap-3 sm:gap-4 p-4">
                    <SessionDate date={eventStart} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-[15px] font-semibold leading-tight truncate min-w-0">{event.title}</h3>
                        {isToday && <Badge className="h-5 px-2 text-xs">{t("events.card.today")}</Badge>}
                        {event.include_guests && (
                          <Badge variant="secondary" className="h-5 px-2 text-xs gap-1">
                            <UserPlus className="h-4 w-4" />
                            {t("events.card.guests")}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-[13px] text-[hsl(var(--faint))]">
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <Clock className="h-3.5 w-3.5" />
                          {eventStart.toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {event.location && (
                          <span className="inline-flex items-center gap-1 truncate max-w-[16ch] sm:max-w-[28ch]">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </span>
                        )}
                        {creator && <span className="truncate">{t("events.card.createdBy", { name: userShortName(creator) })}</span>}
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-3 shrink-0">
                      <CountMark tone="present" label={t("events.card.presentShort")} value={counts.present} />
                      <CountMark tone="late" label={t("events.card.lateShort")} value={counts.late} />
                      <CountMark tone="absent" label={t("events.card.absentShort")} value={counts.absent} />
                      <CountMark tone="pending" label={t("events.card.noResponseShort")} value={counts.no_response} />
                      <div className="text-center min-w-[52px] pl-3 border-l">
                        <p className="font-medium tabular-nums">{callupCount}/{rosterPlayers.length}</p>
                        <p className="text-[13px] text-[hsl(var(--faint))]">{t("events.card.callupsShort")}</p>
                      </div>
                    </div>
                    <div className="md:hidden text-right shrink-0 min-w-[48px]">
                      <p className="font-medium tabular-nums">{counts.present + counts.late}/{rosterPlayers.length}</p>
                      <p className="text-[13px] text-[hsl(var(--faint))]">{t("events.card.presentShort")}</p>
                    </div>
                    <span className="shrink-0 text-[hsl(var(--faint))]">
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </span>
                  </div>
                  <AttendanceBar present={presentPct} late={latePct} absent={absentPct} />
                </button>
                <div className="flex items-center gap-x-3 gap-y-1 px-4 py-2 flex-wrap text-[13px] text-muted-foreground">
                  {deadlineDate && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className={cn("h-3.5 w-3.5", !deadlinePassed && "text-[hsl(var(--warn))]")} />
                      {deadlinePassed
                        ? t("events.card.deadlineClosed")
                        : t("events.card.deadlineOpen", { when: formatDateTime(event.response_deadline_at) })}
                    </span>
                  )}
                  {callupCount > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      {t("events.card.callups")}: <strong className="font-medium tabular-nums text-foreground">{callupCount}</strong>
                    </span>
                  )}
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-11 sm:h-9 px-3 text-sm"
                      disabled={createMagicLink.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        createMagicLink.mutate({ event, mode: "open" });
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t("events.card.openLink")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-11 sm:h-9 px-3 text-sm"
                      disabled={copySessionMessage.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyMessageClick();
                      }}
                    >
                      <MessageSquare className="h-4 w-4" />
                      {t("events.card.message")}
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t">
                    <SessionToolbar
                      event={event}
                      isSavingEdit={updateEvent.isPending}
                      onSaveEdit={(payload) => updateEvent.mutate(payload)}
                      isDuplicating={duplicateEvent.isPending}
                      onDuplicate={() => duplicateEvent.mutate(event)}
                      isCreatingLink={createMagicLink.isPending}
                      onOpenLink={() => createMagicLink.mutate({ event, mode: "open" })}
                      onCopyLink={() => createMagicLink.mutate({ event, mode: "copy" })}
                      isCopyingMessage={copySessionMessage.isPending}
                      onCopyMessage={onCopyMessageClick}
                      copiedMessage={copiedMessageEventId === event.id}
                      isCopyingCallups={copyCallupList.isPending}
                      onCopyCallups={() => {
                        const names = (callupsQuery.data || [])
                          .filter((c) => c.session_id === event.id && c.status === "confirmed")
                          .map((c) => rosterPlayers.find((p) => p.id === c.player_id)?.display_name)
                          .filter((n): n is string => !!n)
                          .sort((a, b) => a.localeCompare(b));
                        copyCallupList.mutate(
                          { event, names },
                          {
                            onSuccess: ({ text, title }) => {
                              setCopiedCallupsEventId(event.id);
                              setShareDialog({ open: true, title, text });
                            },
                          },
                        );
                      }}
                      copiedCallups={copiedCallupsEventId === event.id}
                      callupCount={callupCount}
                      pendingNotifyCount={pendingNotifyCount}
                      isNotifying={notifyCallups.isPending}
                      onNotifyCallups={() => onNotifyCallupsForEvent(event.id, pendingNotifyCount)}
                      isDeleting={deleteEvent.isPending}
                      onDelete={() => {
                        if (window.confirm(t("events.confirmDelete", { title: event.title })))
                          deleteEvent.mutate(event.id);
                      }}
                    />
                    <div className="px-2 pb-3 sm:px-4">
                      <SessionAttendanceBoard
                        event={event}
                        players={rosterPlayers}
                        guestPlayers={guestPlayers}
                        responses={eventResponses}
                        callups={(callupsQuery.data || []).filter((c) => c.session_id === event.id)}
                        searchTerm={searchTerm}
                        isSaving={updateResponse.isPending}
                        isCallupSaving={updateCallup.isPending || callupPresent.isPending}
                        onUpdate={(playerId, status) => updateResponse.mutate({ eventId: event.id, playerId, status })}
                        onToggleCallup={(playerId, shouldCallup) => updateCallup.mutate({ eventId: event.id, playerId, shouldCallup })}
                        onCallupPresent={(playerIds) => callupPresent.mutate({ eventId: event.id, playerIds })}
                        onRefreshResponses={() =>
                          Promise.all([
                            queryClient.invalidateQueries({ queryKey: ["responses", teamId] }),
                            queryClient.invalidateQueries({ queryKey: ["event-callups", teamId] }),
                          ])
                        }
                      />
                    </div>
                  </div>
                )}
              </Card>
            );
          }} />
          {totalPastPages > 1 && (
            <div className="flex justify-center items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPastPage((n) => Math.max(1, n - 1))}
                disabled={safePage === 1}
                className="h-11 sm:h-9 text-sm"
              >
                {t("events.previous")}
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {t("events.page", { page: safePage, total: totalPastPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPastPage((n) => Math.min(totalPastPages, n + 1))}
                disabled={safePage === totalPastPages}
                className="h-11 sm:h-9 text-sm"
              >
                {t("events.next")}
              </Button>
            </div>
          )}
        </div>
      )}
      {pastTraining.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-2.5 items-start">
          <TrainingStatTile label={t("events.stats.done")} value={pastTraining.length} accent="text-foreground" />
          <TrainingStatTile label={t("events.stats.thisMonth")} value={trainingsThisMonth} accent="text-foreground" />
          <TrainingStatTile label={t("events.stats.attendanceRate")} value={`${attendancePct}%`} accent="text-emerald-600" />
          <TrainingStatTile label={t("events.stats.avgCallups")} value={avgCallups} hint={t("events.stats.avgCallupsHint")} accent="text-foreground" />
          <TrainingStatTile
            label={t("events.stats.mostPresent")}
            value={mostPresent?.name || "—"}
            hint={mostPresent ? t("events.stats.presencesHint", { count: mostPresent.value }) : ""}
            ranking={presentRanking}
            rankingHint={(count) => t("events.stats.presencesHint", { count })}
            accent="text-emerald-600"
            small
          />
          <TrainingStatTile
            label={t("events.stats.mostAbsent")}
            value={mostAbsent?.name || "—"}
            hint={mostAbsent ? t("events.stats.absencesHint", { count: mostAbsent.value }) : ""}
            ranking={absentRanking}
            rankingHint={(count) => t("events.stats.absencesHint", { count })}
            accent="text-rose-600"
            small
          />
          <TrainingStatTile
            label={t("events.stats.mostLate")}
            value={mostLate?.name || "—"}
            hint={mostLate ? t("events.stats.latesHint", { count: mostLate.value }) : ""}
            ranking={lateRanking}
            rankingHint={(count) => t("events.stats.latesHint", { count })}
            accent="text-amber-600"
            small
          />
          <TrainingStatTile
            label={t("events.stats.mostNoResponse")}
            value={mostNoResponse?.name || "—"}
            hint={mostNoResponse ? t("events.stats.noResponseHint", { count: mostNoResponse.value }) : ""}
            ranking={noResponseRanking}
            rankingHint={(count) => t("events.stats.noResponseHint", { count })}
            accent="text-foreground"
            small
          />
        </section>
      )}
      <ShareTextDialog
        open={shareDialog.open}
        onOpenChange={(open) => setShareDialog((s) => ({ ...s, open }))}
        title={shareDialog.title}
        text={shareDialog.text}
      />
    </div>
  );
}

type BoardView = "overview" | "list";
const BOARD_VIEW_KEY = "eb.board.view";
const readBoardView = (): BoardView => {
  try {
    return window.localStorage.getItem(BOARD_VIEW_KEY) === "list" ? "list" : "overview";
  } catch {
    return "overview";
  }
};

export function SessionAttendanceBoard({
  event,
  players,
  guestPlayers = [],
  responses,
  callups,
  searchTerm,
  isSaving,
  isCallupSaving,
  onUpdate,
  onToggleCallup,
  onCallupPresent,
  onRefreshResponses,
  singleColumn = false,
}: {
  singleColumn?: boolean;
  event: Session;
  players: Player[];
  guestPlayers?: Player[];
  responses: SessionResponse[];
  callups: SessionCallup[];
  searchTerm: string;
  isSaving: boolean;
  isCallupSaving: boolean;
  onUpdate: (playerId: string, status: ResponseStatus) => void;
  onToggleCallup: (playerId: string, shouldCallup: boolean) => void;
  onCallupPresent: (playerIds: string[]) => void;
  onRefreshResponses?: () => Promise<unknown>;
}) {
  const { t } = useTranslation("sessions");
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Filtro per risposta: si attiva toccando i contatori P/R/A/? in alto.
  const [statusFilter, setStatusFilter] = useState<ResponseStatus | null>(null);
  const [view, setView] = useState<BoardView>(readBoardView);
  const chooseView = (next: BoardView) => {
    setView(next);
    try {
      window.localStorage.setItem(BOARD_VIEW_KEY, next);
    } catch {
      // senza storage la scelta vale solo per questa schermata
    }
  };
  const handleRefresh = async () => {
    if (!onRefreshResponses || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefreshResponses();
    } finally {
      setIsRefreshing(false);
    }
  };
  const responseByPlayer = new Map(responses.map((r) => [r.player_id, r]));
  const callupByPlayer = new Map(callups.map((c) => [c.player_id, c]));
  const rosterIds = new Set(players.map((p) => p.id));
  const rosterResponses = responses.filter((r) => rosterIds.has(r.player_id));

  const buildRow = (player: Player) => ({
    player,
    response: responseByPlayer.get(player.id),
    status: (responseByPlayer.get(player.id)?.status || "no_response") as ResponseStatus,
    callup: callupByPlayer.get(player.id),
  });
  const matchesSearch = (player: Player) => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return true;
    return `${player.display_name} ${player.role_code || ""}`.toLowerCase().includes(needle);
  };
  const matchesFilter = (row: { status: ResponseStatus }) => !statusFilter || row.status === statusFilter;
  // allRows ignora il filtro: serve a "Convoca presenti", che deve
  // convocare tutti i presenti anche se la lista mostra solo gli assenti.
  const allRows = players.filter(matchesSearch).map(buildRow);
  const rows = allRows.filter(matchesFilter);
  const sortByName = (a: typeof rows[number], b: typeof rows[number]) =>
    a.player.display_name.localeCompare(b.player.display_name);
  const calledRows = rows.filter((r) => r.callup?.status === "confirmed").sort(sortByName);
  const otherRows = rows.filter((r) => r.callup?.status !== "confirmed").sort(sortByName);

  // Quando event.include_guests=true, tutti gli ospiti in valutazione sono
  // "invitati" alla sessione anche senza callup. Quando false, mostriamo solo
  // chi ha gia' callup o risposta.
  const guestsInvited = !!event.include_guests;
  const guestRowsAll = guestPlayers
    .filter(matchesSearch)
    .filter((g) =>
      guestsInvited ||
      callupByPlayer.get(g.id)?.status === "confirmed" ||
      responseByPlayer.has(g.id),
    )
    .map(buildRow)
    .sort(sortByName);
  const guestRowsVisible = guestRowsAll.filter(matchesFilter);
  const guestCalledRows = guestRowsVisible.filter((r) => r.callup?.status === "confirmed");
  const guestInvitedOnlyRows = guestRowsVisible.filter((r) => r.callup?.status !== "confirmed");
  const guestPresentCount = guestRowsAll.filter((r) => r.status === "present" || r.status === "late").length;
  const availableGuests = (guestsInvited ? guestPlayers : guestPlayers)
    .filter((g) => callupByPlayer.get(g.id)?.status !== "confirmed")
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  const counts = getResponseCounts(rosterResponses, players.length);
  const presentIds = allRows
    .filter((row) => row.status === "present" || row.status === "late")
    .map((row) => row.player.id);
  const confirmedCallups = callups.filter(
    (c) => c.status === "confirmed" && rosterIds.has(c.player_id),
  ).length;
  // "Ci sono altri?": chi puo' esserci e non e' ancora convocato, a prescindere
  // da filtro e ricerca.
  const otherAvailable = players.filter((p) => {
    const status = responseByPlayer.get(p.id)?.status;
    return (status === "present" || status === "late") && callupByPlayer.get(p.id)?.status !== "confirmed";
  }).length;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex gap-[3px] h-2 sm:h-6" aria-hidden>
          {([
            ...Array(counts.present).fill("bg-[hsl(var(--ok))]"),
            ...Array(counts.late).fill("bg-[hsl(var(--warn))]"),
            ...Array(counts.absent).fill("bg-[hsl(var(--danger))]"),
            ...Array(counts.no_response).fill("bg-raised"),
          ] as string[]).map((tone, index) => <span key={index} className={cn("flex-1 rounded-[3px]", tone)} />)}
        </div>
        <div className="mt-1.5 sm:mt-3 grid grid-cols-4 gap-1">
          {([
            ["present", "dot-ok", t("board.counterPresent")],
            ["late", "dot-warn", t("board.counterLate")],
            ["absent", "dot-danger", t("board.counterAbsent")],
            ["no_response", "dot-muted", t("board.counterPending")],
          ] as const).map(([status, dot, label]) => {
            const active = statusFilter === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(active ? null : status)}
                aria-pressed={active}
                title={active ? t("board.filterShowAll") : t("board.filterOnly", { status: statusLabel(status) })}
                className={cn("rounded-2xl px-2 py-1 sm:py-2 text-left transition-colors", active ? "bg-[hsl(var(--active))]" : "hover:bg-raised")}
              >
                <span className="flex items-center gap-1.5">
                  <span className={cn("eb-dot", dot)} />
                  <strong className="eb-num text-[24px] sm:text-[34px] pr-1">{counts[status]}</strong>
                </span>
                <span className="block text-[13px] text-[hsl(var(--faint))] truncate">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div role="group" aria-label={t("board.viewLabel")} className="inline-flex rounded-full bg-raised p-0.5">
          {(["overview", "list"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => chooseView(option)}
              className={cn(
                "h-10 sm:h-8 rounded-full px-2.5 sm:px-3.5 text-sm font-semibold transition-colors",
                view === option ? "bg-[hsl(var(--active))] text-foreground" : "text-muted-foreground",
              )}
            >
              {t(option === "overview" ? "board.viewOverview" : "board.viewList")}
            </button>
          ))}
        </div>
        <div className="flex items-center sm:gap-2">
          {onRefreshResponses && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 sm:h-9 px-2 sm:px-3 text-sm gap-1.5"
              disabled={isRefreshing}
              onClick={handleRefresh}
              title={t("board.refreshResponses")}
              aria-label={t("board.refreshResponses")}
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              <span className="hidden sm:inline">{t("board.refreshResponses")}</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-11 sm:h-9 px-3 sm:px-4 text-sm"
            disabled={isCallupSaving || presentIds.length === 0}
            onClick={() => onCallupPresent(presentIds)}
          >
            {t("board.callupPresent")}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap px-1 text-[13px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground tabular-nums">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {t("board.calledSection", { count: confirmedCallups })}
        </span>
        <span className="font-medium text-foreground tabular-nums">{t("board.otherAvailable", { count: otherAvailable })}</span>
          {guestRowsAll.length > 0 && (
          <span
              title={
                guestsInvited
                  ? `Ospiti invitati (${guestCalledRows.length} convocati / ${guestPresentCount} disponibili)`
                  : `Ospiti convocati / presenti`
              }
            >
              <strong className="tabular-nums">+{guestRowsAll.length}</strong> osp
              {" "}({guestCalledRows.length}C · {guestPresentCount}P)
            </span>
        )}
        {view === "overview" && <span className="text-[hsl(var(--faint))]">{t("board.overviewHint")}</span>}
      </div>
      <div>
        {view === "overview" && (
          <CallupOverview rows={rows} allRows={allRows} twoColumns={singleColumn} onToggle={onToggleCallup} />
        )}
        {view === "list" && calledRows.length > 0 && (
          <>
            <h4 className="px-2 py-2 font-semibold">{t("board.calledSection", { count: calledRows.length })}</h4>
            <div className={cn(!singleColumn && "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:[&>*:nth-child(2)]:border-t-0")}>
              {calledRows.map(({ player, status, callup }, idx) => (
                <SessionAttendanceRow
                  key={`${event.id}-${player.id}`}
                  player={player}
                  status={status}
                  isCalled={callup?.status === "confirmed"}
                  callupNotified={!!callup?.notified_at}
                  isSaving={isSaving}
                  isCallupSaving={isCallupSaving}
                  onUpdate={onUpdate}
                  onToggleCallup={onToggleCallup}
                  showBorder={idx > 0}
                />
              ))}
            </div>
          </>
        )}
        {view === "list" && otherRows.length > 0 && (
          <>
            <h4 className={cn("px-2 py-2 font-semibold", calledRows.length > 0 && "mt-3")}>{t("board.restSection", { count: otherRows.length })}</h4>
            <div className={cn(!singleColumn && "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:[&>*:nth-child(2)]:border-t-0")}>
              {otherRows.map(({ player, status, callup }, idx) => (
                <SessionAttendanceRow
                  key={`${event.id}-${player.id}`}
                  player={player}
                  status={status}
                  isCalled={callup?.status === "confirmed"}
                  callupNotified={!!callup?.notified_at}
                  isSaving={isSaving}
                  isCallupSaving={isCallupSaving}
                  onUpdate={onUpdate}
                  onToggleCallup={onToggleCallup}
                  showBorder={idx > 0}
                />
              ))}
            </div>
          </>
        )}
        {rows.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {statusFilter ? t("board.noPlayersForFilter", { status: statusLabel(statusFilter) }) : t("board.noPlayers")}
          </div>
        )}
        {(guestRowsAll.length > 0 || availableGuests.length > 0) && (
          <div className="mt-3">
            <div className="px-2 py-2 flex items-baseline justify-between gap-3 flex-wrap">
              <span className="font-semibold">
                {guestsInvited ? t("board.guestsInvited") : t("board.guestsCalledUp")} · {guestRowsVisible.length}
              </span>
              {guestsInvited && (
                <span className="text-[13px] text-[hsl(var(--faint))]">
                  {t("board.guestsManualNote")}
                </span>
              )}
            </div>
            {view === "overview" && (
              <CallupTiles rows={guestRowsVisible} twoColumns={singleColumn} onToggle={onToggleCallup} />
            )}
            {view === "list" && guestCalledRows.length > 0 && (
              <>
                {guestsInvited && (
                  <div className="px-2 py-1 text-[13px] text-[hsl(var(--faint))]">
                    {t("board.guestsCalledSection", { count: guestCalledRows.length })}
                  </div>
                )}
                <div className={cn(!singleColumn && "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:[&>*:nth-child(2)]:border-t-0")}>
                  {guestCalledRows.map(({ player, status, callup }, idx) => (
                    <SessionAttendanceRow
                      key={`${event.id}-called-${player.id}`}
                      player={player}
                      status={status}
                      isCalled={callup?.status === "confirmed"}
                      callupNotified={!!callup?.notified_at}
                      isSaving={isSaving}
                      isCallupSaving={isCallupSaving}
                      onUpdate={onUpdate}
                      onToggleCallup={onToggleCallup}
                      showBorder={idx > 0}
                      isGuest
                    />
                  ))}
                </div>
              </>
            )}
            {view === "list" && guestsInvited && guestInvitedOnlyRows.length > 0 && (
              <>
                <div className="px-2 py-1 text-[13px] text-[hsl(var(--faint))]">
                  {t("board.guestsInvitedOnly", { count: guestInvitedOnlyRows.length })}
                </div>
                <div className={cn(!singleColumn && "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:[&>*:nth-child(2)]:border-t-0")}>
                  {guestInvitedOnlyRows.map(({ player, status, callup }, idx) => (
                    <SessionAttendanceRow
                      key={`${event.id}-invited-${player.id}`}
                      player={player}
                      status={status}
                      isCalled={callup?.status === "confirmed"}
                      callupNotified={!!callup?.notified_at}
                      isSaving={isSaving}
                      isCallupSaving={isCallupSaving}
                      onUpdate={onUpdate}
                      onToggleCallup={onToggleCallup}
                      showBorder={idx > 0}
                      isGuest
                    />
                  ))}
                </div>
              </>
            )}
            {!guestsInvited && availableGuests.length > 0 && (
              <div
                className={cn(
                  "px-2 py-2 flex items-center gap-2",
                )}
              >
                <span className="text-[13px] text-muted-foreground shrink-0">{t("board.callGuest")}</span>
                <Select
                  value=""
                  onValueChange={(playerId) => {
                    if (!playerId) return;
                    onToggleCallup(playerId, true);
                  }}
                >
                  <SelectTrigger className="flex-1 min-w-0">
                    <SelectValue placeholder={t("board.selectGuest")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableGuests.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.display_name}
                        {g.role_code ? ` · ${g.role_code}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionAttendanceRow({
  player,
  status,
  isCalled,
  callupNotified,
  isSaving,
  isCallupSaving,
  onUpdate,
  onToggleCallup,
  showBorder,
  isGuest,
}: {
  player: Player;
  status: ResponseStatus;
  isCalled: boolean;
  callupNotified?: boolean;
  isSaving: boolean;
  isCallupSaving: boolean;
  onUpdate: (playerId: string, status: ResponseStatus) => void;
  onToggleCallup: (playerId: string, shouldCallup: boolean) => void;
  showBorder?: boolean;
  isGuest?: boolean;
}) {
  const { t } = useTranslation("sessions");
  const callupTitle = isCalled
    ? callupNotified
      ? t("board.callupAlreadyNotified")
      : t("board.callupPendingNotify")
    : t("board.addCallup");
  return (
    <div className={cn("flex items-center gap-x-2 sm:gap-x-3 px-1 sm:px-2 sm:py-2.5", showBorder && "border-t")}>
      <span className="eb-num text-[18px] sm:text-[22px] w-6 sm:w-7 shrink-0 text-center text-muted-foreground">{player.shirt_number ?? "-"}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate leading-tight">
          {player.display_name}
          {isGuest && <span className="ml-1.5 text-[13px] font-normal text-[hsl(var(--warn))]">{t("matchPanel.guestTag")}</span>}
        </span>
        <span className="hidden sm:block text-[13px] text-[hsl(var(--faint))] leading-tight">{player.role_code || "\u00a0"}</span>
      </span>
      <span className="sm:hidden shrink-0 text-[13px] text-[hsl(var(--faint))]">{player.role_code}</span>
      <span
        role="img"
        aria-label={statusLabel(status)}
        className={cn(
          "hidden sm:inline-block eb-dot h-2.5 w-2.5",
          status === "present" && "dot-ok",
          status === "late" && "dot-warn",
          status === "absent" && "dot-danger",
          status === "no_response" && "dot-muted",
        )}
      />
      <div className="shrink-0 flex items-center sm:gap-2">
        <div className="hidden sm:block">
          <ResponsePillGroup status={status} disabled={isSaving} onChange={(next) => onUpdate(player.id, next)} />
        </div>
        <div className="sm:hidden">
          <ResponseMenu status={status} disabled={isSaving} onChange={(next) => onUpdate(player.id, next)} />
        </div>
        <button
          type="button"
          disabled={isCallupSaving}
          onClick={() => onToggleCallup(player.id, !isCalled)}
          aria-pressed={isCalled}
          className={cn(
            "h-11 w-11 sm:h-9 sm:w-9 rounded-full text-[13px] font-semibold transition-colors inline-flex items-center justify-center",
            isCalled
              ? callupNotified
                ? "bg-accent text-accent-foreground"
                : "bg-accent/25 ring-1 ring-inset ring-accent"
              : "bg-raised text-muted-foreground hover:bg-[hsl(var(--active))]",
          )}
          title={callupTitle}
        >
          C
        </button>
      </div>
    </div>
  );
}

const RESPONSE_CHOICES: Array<{ key: ResponseStatus; label: string; activeCls: string }> = [
  { key: "present", label: "P", activeCls: "bg-[hsl(var(--ok))] text-background" },
  { key: "late", label: "R", activeCls: "bg-[hsl(var(--warn))] text-background" },
  { key: "absent", label: "A", activeCls: "bg-[hsl(var(--danger))] text-background" },
  { key: "no_response", label: "?", activeCls: "bg-[hsl(var(--faint))] text-background" },
];

function ResponseMenu({
  status,
  disabled,
  onChange,
}: {
  status: ResponseStatus;
  disabled?: boolean;
  onChange: (next: ResponseStatus) => void;
}) {
  const { t } = useTranslation("sessions");
  const current = RESPONSE_CHOICES.find((c) => c.key === status)!;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        disabled={disabled}
        aria-label={t("board.changeStatus", { status: statusLabel(status) })}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full"
      >
        <span className={cn("inline-flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold", current.activeCls)}>
          {current.label}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {RESPONSE_CHOICES.map((c) => (
          <DropdownMenuItem key={c.key} className="min-h-11 gap-2" onClick={() => onChange(c.key)}>
            <span className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold", c.activeCls)}>{c.label}</span>
            {statusLabel(c.key)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ResponsePillGroup({
  status,
  disabled,
  onChange,
}: {
  status: ResponseStatus;
  disabled?: boolean;
  onChange: (next: ResponseStatus) => void;
}) {
  return (
    <div className="inline-flex rounded-full bg-raised p-0.5">
      {RESPONSE_CHOICES.map((c) => {
        const active = status === c.key;
        return (
          <button
            key={c.key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(c.key)}
            className={cn(
              "h-9 w-9 rounded-full text-[13px] font-semibold transition-colors",
              active ? c.activeCls : "text-muted-foreground hover:bg-[hsl(var(--active))]"
            )}
            title={statusLabel(c.key)}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function TrainingStatTile({
  label,
  value,
  hint,
  accent,
  small = false,
  ranking,
  rankingHint,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: string;
  small?: boolean;
  // Se presente e con piu' di un nome, il riquadro si espande sulla top 5.
  ranking?: { name: string; value: number }[];
  rankingHint?: (count: number) => string;
}) {
  const { t } = useTranslation("sessions");
  const [open, setOpen] = useState(false);
  const expandable = !!ranking && ranking.length > 1;
  return (
    // Tutto il riquadro apre/chiude la classifica: su mobile il chevron da
    // solo e' un bersaglio piccolo.
    <div
      className={cn("rounded-2xl bg-card px-4 py-3", expandable && "cursor-pointer")}
      onClick={expandable ? () => setOpen((v) => !v) : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <StatTileLabel>{label}</StatTileLabel>
        {expandable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            aria-expanded={open}
            aria-label={open ? t("events.stats.hideRanking") : t("events.stats.showRanking")}
            className="-mr-2 -my-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>
      <p
        className={cn(
          "truncate",
          small ? "font-semibold mt-1" : "eb-num text-[34px] mt-1.5 pr-1",
          accent,
        )}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </p>
      {hint && <p className="text-[13px] text-[hsl(var(--faint))] mt-0.5 truncate">{hint}</p>}
      {expandable && open && (
        <ol className="mt-2 border-t pt-2 space-y-1">
          {ranking!.map((r, i) => (
            <li key={r.name + i} className="flex items-baseline gap-2 text-[13px]">
              {/* A pari valore stessa posizione (1, 1, 3...). */}
              <span className="w-4 shrink-0 text-right tabular-nums text-muted-foreground">
                {ranking!.findIndex((x) => x.value === r.value) + 1}
              </span>
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground" title={rankingHint?.(r.value)}>{r.value}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SessionEditor({
  event,
  isSaving,
  onSave,
}: {
  event: Session;
  isSaving: boolean;
  onSave: (payload: { eventId: string; title: string; date: string; time: string; deadlineDate: string; deadlineTime: string; location: string; notes: string; status: string }) => void;
}) {
  const { t } = useTranslation("sessions");
  const toLocalDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("sv-SE") : "");
  const toLocalTime = (iso: string | null) => (iso ? new Date(iso).toTimeString().slice(0, 5) : "");
  const initialDate = toLocalDate(event.starts_at);
  const initialTime = toLocalTime(event.starts_at);
  const initialDeadlineDate = toLocalDate(event.response_deadline_at || event.starts_at);
  const initialDeadlineTime = toLocalTime(event.response_deadline_at || event.starts_at);
  const [title, setTitle] = useState(event.title);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [deadlineDate, setDeadlineDate] = useState(initialDeadlineDate);
  const [deadlineTime, setDeadlineTime] = useState(initialDeadlineTime);
  const [location, setLocation] = useState(event.location || "");
  const [notes, setNotes] = useState(event.notes || "");
  const [status, setStatus] = useState(event.status);

  useEffect(() => {
    setTitle(event.title);
    // Ricalcoliamo da `event.starts_at`/`event.response_deadline_at` invece di
    // riusare le `initial*` di chiusura: cosi' le deps sono complete
    // (react-hooks/exhaustive-deps) senza workaround.
    setDate(toLocalDate(event.starts_at));
    setTime(toLocalTime(event.starts_at));
    setDeadlineDate(toLocalDate(event.response_deadline_at || event.starts_at));
    setDeadlineTime(toLocalTime(event.response_deadline_at || event.starts_at));
    setLocation(event.location || "");
    setNotes(event.notes || "");
    setStatus(event.status);
  }, [event.id, event.title, event.starts_at, event.response_deadline_at, event.location, event.notes, event.status]);

  return (
    <Card className="bg-transparent">
      <CardContent className="p-0">
        <form
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            onSave({ eventId: event.id, title, date, time, deadlineDate, deadlineTime, location, notes, status });
          }}
        >
          <div className="space-y-1.5 lg:col-span-2">
            <Label>{t("editor.title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.state")}</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{t("editor.stateDraft")}</SelectItem>
                <SelectItem value="scheduled">{t("editor.stateScheduled")}</SelectItem>
                <SelectItem value="closed">{t("editor.stateClosed")}</SelectItem>
                <SelectItem value="cancelled">{t("editor.stateCancelled")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.date")}</Label>
            <Input value={date} onChange={(e) => setDate(e.target.value)} type="date" required />
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.time")}</Label>
            <Input value={time} onChange={(e) => setTime(e.target.value)} type="time" required />
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.location")}</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.deadlineDate")}</Label>
            <Input value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} type="date" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("editor.deadlineTime")}</Label>
            <Input value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} type="time" />
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
            <Label>{t("editor.notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
            <Button type="submit" variant="default" disabled={isSaving}>
              {t("editor.save")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function getResponseCounts(responses: SessionResponse[], expectedPlayers = responses.length) {
  const counts = responses.reduce(
    (acc, response) => {
      acc[response.status] += 1;
      return acc;
    },
    { present: 0, late: 0, absent: 0, no_response: 0 } as Record<ResponseStatus, number>
  );
  counts.no_response += Math.max(0, expectedPlayers - responses.length);
  return counts;
}

export function SessionToolbar({
  event,
  isSavingEdit,
  onSaveEdit,
  isDuplicating,
  onDuplicate,
  isCreatingLink,
  onOpenLink,
  onCopyLink,
  isCopyingMessage,
  onCopyMessage,
  copiedMessage,
  isCopyingCallups,
  onCopyCallups,
  copiedCallups,
  callupCount,
  pendingNotifyCount,
  isNotifying,
  onNotifyCallups,
  isDeleting,
  onDelete,
}: {
  event: Session;
  isSavingEdit: boolean;
  onSaveEdit: (payload: { eventId: string; title: string; date: string; time: string; deadlineDate: string; deadlineTime: string; location: string; notes: string; status: string }) => void;
  isDuplicating: boolean;
  onDuplicate: () => void;
  isCreatingLink: boolean;
  onOpenLink: () => void;
  onCopyLink: () => void;
  isCopyingMessage: boolean;
  onCopyMessage: () => void;
  copiedMessage: boolean;
  isCopyingCallups: boolean;
  onCopyCallups: () => void;
  copiedCallups: boolean;
  callupCount: number;
  pendingNotifyCount: number;
  isNotifying: boolean;
  onNotifyCallups: () => void;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation("sessions");
  const [editOpen, setEditOpen] = useState(false);
  const deadlinePassed = !!event.response_deadline_at && new Date(event.response_deadline_at).getTime() < Date.now();
  return (
    <div className="flex items-center gap-2 px-3 py-3 sm:px-4 flex-wrap">
      {!deadlinePassed && (
        <Button
          size="sm"
          variant="default"
          className="h-11 sm:h-9 text-sm"
          disabled={isCreatingLink}
          onClick={onCopyMessage}
        >
          <MessageSquare className="h-4 w-4" />
          {copiedMessage ? t("board.whatsAppCopied") : t("board.whatsAppMessage")}
        </Button>
      )}
      {callupCount > 0 && (
        <Button
          size="sm"
          variant={deadlinePassed ? "default" : "outline"}
          className="h-11 sm:h-9 text-sm"
          disabled={isCopyingCallups}
          onClick={onCopyCallups}
          title={t("board.callupsWhatsApp", { count: callupCount })}
        >
          <MessageSquare className="h-4 w-4" />
          {copiedCallups ? t("board.callupsCopied") : t("board.callupsWhatsApp", { count: callupCount })}
        </Button>
      )}
      {callupCount > 0 && (
        <Button
          size="sm"
          variant={pendingNotifyCount > 0 ? "default" : "outline"}
          className="h-11 sm:h-9 text-sm"
          disabled={isNotifying || pendingNotifyCount === 0}
          onClick={onNotifyCallups}
          title={
            pendingNotifyCount === 0
              ? t("board.notifyCallupsAllSent")
              : t("board.notifyCallupsWithCount", { count: pendingNotifyCount })
          }
        >
          <Bell className="h-4 w-4" />
          {isNotifying
            ? t("board.notifyCallupsSending")
            : pendingNotifyCount === 0
              ? t("board.notifyCallupsAllSent")
              : t("board.notifyCallupsWithCount", { count: pendingNotifyCount })}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-11 w-11 sm:h-9 sm:w-9 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" /> {t("editor.menuEditInfo")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenLink} disabled={isCreatingLink}>
            <ExternalLink className="h-4 w-4 mr-2" /> {t("editor.menuOpenLink")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCopyLink} disabled={isCreatingLink}>
            <Copy className="h-4 w-4 mr-2" /> {t("editor.menuCopyLink")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDuplicate} disabled={isDuplicating}>
            <Copy className="h-4 w-4 mr-2" /> {t("editor.menuDuplicate")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDelete}
            disabled={isDeleting}
            className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]"
          >
            <Trash2 className="h-4 w-4 mr-2" /> {t("editor.menuDelete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("editor.infoTitle")}</DialogTitle>
          </DialogHeader>
          <SessionEditor
            event={event}
            isSaving={isSavingEdit}
            onSave={(payload) => {
              onSaveEdit(payload);
              setEditOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
